import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { authApi, userApi } from '../utils/api';
import type { UserInfo } from '../types/api';

interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  token: string | null;
}

interface AuthContextType extends AuthState {
  login: (account: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (account: string, code: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  // 刷新用户信息
  const refreshUser = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    try {
      const response = await userApi.getInfo();
      if (response.code === 1 && response.data) {
        const user = response.data as UserInfo;
        setAuthState({
          isAuthenticated: true,
          user,
          token,
        });
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        // Token无效，清除登录状态
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
        });
      }
    } catch {
      // 网络错误时，保持localStorage中的状态，下次再验证
      const userStr = localStorage.getItem(USER_KEY);
      if (userStr) {
        try {
          const user = JSON.parse(userStr) as UserInfo;
          setAuthState({
            isAuthenticated: true,
            user,
            token,
          });
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      }
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token) {
        // 验证token是否有效
        await refreshUser();
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (account: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.accountLogin(account, password);

      if (response.code === 1 && response.data) {
        const { userinfo, token } = response.data;

        setAuthState({
          isAuthenticated: true,
          user: userinfo as UserInfo,
          token,
        });

        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(userinfo));

        setIsLoading(false);
        return { success: true, message: 'Login successful' };
      }

      setIsLoading(false);
      return { success: false, message: response.msg || 'Login failed' };
    } catch {
      setIsLoading(false);
      return { success: false, message: 'Network error, please try again' };
    }
  };

  const register = async (account: string, code: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.register({ account, captcha: code, password, platform: 'H5' });

      if (response.code === 1 && response.data) {
        const { userinfo, token } = response.data;

        setAuthState({
          isAuthenticated: true,
          user: userinfo as UserInfo,
          token,
        });

        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(userinfo));

        setIsLoading(false);
        return { success: true, message: 'Registration successful' };
      }

      setIsLoading(false);
      return { success: false, message: response.msg || 'Registration failed' };
    } catch {
      setIsLoading(false);
      return { success: false, message: 'Network error, please try again' };
    }
  };

  const logout = () => {
    authApi.logout().catch(() => {});

    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
    });

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, register, logout, refreshUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
